
import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { PartSubmission } from "@/types/submission";

const partCategories = [
  { value: "k1_series", label: "K1 Series" },
  { value: "k2_series", label: "K2 Series" },
  { value: "tools", label: "Tools" }
];

const partSubcategories = [
  { value: "hotend", label: "Hotend" },
  { value: "extruder", label: "Extruder" },
  { value: "probe", label: "Probe" },
  { value: "tools", label: "Tools" },
  { value: "fan", label: "Fan" }
];

const filters = [
  { value: "upgrade", label: "Upgrade" },
  { value: "eddy-current", label: "Eddy Current" },
  { value: "clicky", label: "Clicky" },
  { value: "unicorn", label: "Unicorn" },
  { value: "stock", label: "Stock" }
];

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  description: z.string().min(10, { message: "Description must be at least 10 characters." }),
  category: z.string().optional(),
  subcategory: z.string({ required_error: "Subcategory is required" }),
  difficulty: z.number().min(1).max(5),
  rating: z.number().min(1).max(5),
  filters: z.array(z.string()).min(1, { message: "Select at least one filter" }),
  manufacturer: z.string().optional(),
  amazon_link: z.string().url({ message: "Must be a valid URL" }).optional().or(z.literal("")),
  ali_express_link: z.string().url({ message: "Must be a valid URL" }).optional().or(z.literal("")),
  ali_express_link_us: z.string().url({ message: "Must be a valid URL" }).optional().or(z.literal("")),
  video_guide: z.string().url({ message: "Must be a valid URL" }).optional().or(z.literal("")),
  written_guide: z.string().url({ message: "Must be a valid URL" }).optional().or(z.literal("")),
  requires_wiring: z.boolean().default(false),
  requires_root: z.boolean().default(false),
});

interface PartSubmissionFormProps {
  onSubmit: (data: PartSubmission) => void;
  isSubmitting: boolean;
}

const PartSubmissionForm = ({ onSubmit, isSubmitting }: PartSubmissionFormProps) => {
  const form = useForm<PartSubmission>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      category: undefined,
      subcategory: "",
      difficulty: 3,
      rating: 3,
      filters: [],
      manufacturer: "",
      amazon_link: "",
      ali_express_link: "",
      ali_express_link_us: "",
      video_guide: "",
      written_guide: "",
      requires_wiring: false,
      requires_root: false,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Part Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter part name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Describe the part and its benefits" 
                  className="min-h-[100px]" 
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Category</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {partCategories.map((category) => (
                      <SelectItem key={category.value} value={category.value}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="subcategory"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Subcategory</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select subcategory" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {partSubcategories.map((subcategory) => (
                      <SelectItem key={subcategory.value} value={subcategory.value}>
                        {subcategory.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="difficulty"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Difficulty (1-5): {field.value}</FormLabel>
                <FormControl>
                  <Slider
                    min={1}
                    max={5}
                    step={1}
                    value={[field.value]}
                    onValueChange={(value) => field.onChange(value[0])}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="rating"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rating (1-5): {field.value}</FormLabel>
                <FormControl>
                  <Slider
                    min={1}
                    max={5}
                    step={1}
                    value={[field.value]}
                    onValueChange={(value) => field.onChange(value[0])}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="filters"
          render={() => (
            <FormItem>
              <FormLabel>Filters</FormLabel>
              <div className="flex flex-wrap gap-4">
                {filters.map((filter) => (
                  <FormField
                    key={filter.value}
                    control={form.control}
                    name="filters"
                    render={({ field }) => {
                      return (
                        <FormItem
                          key={filter.value}
                          className="flex flex-row items-start space-x-3 space-y-0"
                        >
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(filter.value)}
                              onCheckedChange={(checked) => {
                                return checked
                                  ? field.onChange([...field.value, filter.value])
                                  : field.onChange(
                                      field.value?.filter(
                                        (value) => value !== filter.value
                                      )
                                    )
                              }}
                            />
                          </FormControl>
                          <FormLabel className="font-normal cursor-pointer">
                            {filter.label}
                          </FormLabel>
                        </FormItem>
                      )
                    }}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="manufacturer"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Manufacturer</FormLabel>
              <FormControl>
                <Input placeholder="(Optional) Manufacturer name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="amazon_link"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Amazon Link</FormLabel>
                <FormControl>
                  <Input placeholder="(Optional) Amazon product URL" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="ali_express_link"
            render={({ field }) => (
              <FormItem>
                <FormLabel>AliExpress Link</FormLabel>
                <FormControl>
                  <Input placeholder="(Optional) AliExpress product URL" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="video_guide"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Video Guide</FormLabel>
                <FormControl>
                  <Input placeholder="(Optional) YouTube or video URL" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="written_guide"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Written Guide</FormLabel>
                <FormControl>
                  <Input placeholder="(Optional) URL to written instructions" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="requires_wiring"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="font-normal cursor-pointer">
                  Requires wiring modifications
                </FormLabel>
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="requires_root"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="font-normal cursor-pointer">
                  Requires root access
                </FormLabel>
              </FormItem>
            )}
          />
        </div>

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : "Submit Part for Review"}
        </Button>
      </form>
    </Form>
  );
};

export default PartSubmissionForm;
